import { useCallback, useRef, useEffect } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import { useTrackOperations } from "../../../utils/useTrackOperations";
import { UNSPOTTED_TRACK_ID } from "../../../utils/constants";
import {
  calculateDragIndex,
  hasPositionChanged,
} from "../../../utils/trackDnDUtils";

const CROSS_TAB_REFRESH_KEY = "track-capacity-refresh";
const CROSS_TAB_CHANNEL = "track-capacity-channel";
const CROSS_TAB_FRESHNESS_MS = 60_000;

export const useTrackDragDrop = ({
  processedCarsMap,
  setProcessedCarsMap,
  selectedShipmentIds,
  setSelectedShipmentIds,
  setLoading,
  saveShipment,
  notify,
  logError,
  redirect,
  setDragOverInfo,
  unspottedTrackId = UNSPOTTED_TRACK_ID,
  refreshTracks,
  getShipments,
  onSuccess,
  appUser,
  updateShipmentOnPlacement,
  /** Optional manager-only ref: set to timestamp when a move persists. */
  justCompletedDragRef,
  tracks,
  /** Optional: use this for setTrackData so ref stays in sync after move (avoids stale merge). */
  setTrackData = null,
}) => {
  const { handleTrackDrop: handleTrackDropBase } = useTrackOperations({
    trackData: processedCarsMap,
    setTrackData: setTrackData ?? setProcessedCarsMap,
    setLoading,
    saveShipment,
    notify,
    logError,
    redirect,
    onSuccess: () => {
      setSelectedShipmentIds([]);
      if (justCompletedDragRef) justCompletedDragRef.current = Date.now();
      if (onSuccess) onSuccess();
    },
    unspottedTrackId,
    refreshTracks,
    getShipments,
    appUser,
    updateShipmentOnPlacement,
    tracks,
  });

  const originalStateRef = useRef(null);
  const latestStateRef = useRef(processedCarsMap);
  // CRITICAL: Keep ref in sync during render to prevent stale data during rapid drag starts
  latestStateRef.current = processedCarsMap;

  // CRITICAL: Prevent multiple simultaneous state updates
  const isUpdatingRef = useRef(false);
  // CRITICAL: Throttle drag over events for mobile performance
  const lastUpdateTimeRef = useRef(0);
  // CRITICAL: Prevent multiple simultaneous saves
  const isSavingRef = useRef(false);
  const selectedShipmentIdsRef = useRef(selectedShipmentIds);
  const refreshTracksRef = useRef(refreshTracks);
  const broadcastChannelRef = useRef(null);

  // Keep refresh function updated
  useEffect(() => {
    refreshTracksRef.current = refreshTracks;
  }, [refreshTracks]);

  // Cross-tab listener so other tabs refresh affected tracks
  useEffect(() => {
    const handleRefresh = (ids) => {
      if (Array.isArray(ids) && ids.length > 0) {
        refreshTracksRef.current?.(ids);
      } else {
        refreshTracksRef.current?.();
      }
    };

    // Prefer BroadcastChannel when available for more reliable cross-tab messaging
    let channel = null;
    const onMessage = (event) => {
      const payload = event?.data;
      if (!payload) return;
      const { ids, ts } = payload;
      if (!ts || Date.now() - ts > CROSS_TAB_FRESHNESS_MS) return;
      handleRefresh(ids);
    };

    if (typeof window !== "undefined" && "BroadcastChannel" in window) {
      channel = new BroadcastChannel(CROSS_TAB_CHANNEL);
      broadcastChannelRef.current = channel;
      channel.addEventListener("message", onMessage);
    }

    const handler = (event) => {
      if (event.key !== CROSS_TAB_REFRESH_KEY || !event.newValue) return;
      try {
        const payload = JSON.parse(event.newValue);
        const { ids, ts } = payload;
        // Ignore very old messages or malformed payloads
        if (!ts || Date.now() - ts > CROSS_TAB_FRESHNESS_MS) return;
        handleRefresh(ids);
      } catch (err) {
        // Swallow parsing errors
      }
    };
    window.addEventListener("storage", handler);

    return () => {
      window.removeEventListener("storage", handler);
      if (channel) {
        channel.removeEventListener("message", onMessage);
        channel.close();
      }
    };
  }, []);

  useEffect(() => {
    latestStateRef.current = processedCarsMap;
  }, [processedCarsMap]);

  useEffect(() => {
    selectedShipmentIdsRef.current = selectedShipmentIds;
  }, [selectedShipmentIds]);

  const findContainerRef = useRef(null);
  const containerCacheRef = useRef(new Map());

  const getNextStateForMove = useCallback(
    ({
      prev,
      active,
      over,
      overId,
      activeContainer,
      overContainer,
      shipmentIdsToMove,
      isMultiDrag,
    }) => {
      const currentActiveItems = prev[activeContainer] || [];
      const currentOverItems = prev[overContainer] || [];

      const currentActiveIndex = currentActiveItems.findIndex(
        (c) => c.id === active.id,
      );
      if (currentActiveIndex === -1) return prev;

      const targetIndex = calculateDragIndex({
        overId,
        overItems: currentOverItems,
        active,
        over,
        threshold: 0.5,
      });

      if (targetIndex === null) return prev;

      if (
        !hasPositionChanged({
          activeContainer,
          overContainer,
          activeIndex: currentActiveIndex,
          newIndex: targetIndex,
          isMultiDrag,
        })
      ) {
        return prev;
      }

      if (activeContainer === overContainer && !isMultiDrag) {
        if (
          currentActiveIndex === targetIndex ||
          (String(overId).startsWith("track-") &&
            currentActiveIndex === currentActiveItems.length - 1)
        ) {
          return prev;
        }
      }

      if (activeContainer !== overContainer) {
        const movingItemsSet = new Set(shipmentIdsToMove);
        const movingItems = [];
        const affectedSourceTracks = {};

        const itemsMovingFromActive = currentActiveItems.filter((item) =>
          movingItemsSet.has(item.id),
        );
        if (itemsMovingFromActive.length > 0) {
          movingItems.push(...itemsMovingFromActive);
          affectedSourceTracks[activeContainer] = currentActiveItems.filter(
            (item) => !movingItemsSet.has(item.id),
          );
        }

        const targetBaseItems =
          affectedSourceTracks[overContainer] !== undefined
            ? affectedSourceTracks[overContainer]
            : prev[overContainer] || [];

        const newTargetBase = targetBaseItems.filter(
          (item) => !movingItemsSet.has(item.id),
        );

        const safeIndex = Math.min(targetIndex, newTargetBase.length);
        const newTarget = [
          ...newTargetBase.slice(0, safeIndex),
          ...movingItems,
          ...newTargetBase.slice(safeIndex),
        ];

        return {
          ...prev,
          ...affectedSourceTracks,
          [overContainer]: newTarget,
        };
      }

      if (isMultiDrag) {
        const movingItemsSet = new Set(shipmentIdsToMove);
        const movingItems = currentActiveItems.filter((item) =>
          movingItemsSet.has(item.id),
        );
        const remainingItems = currentActiveItems.filter(
          (item) => !movingItemsSet.has(item.id),
        );

        let adjustedIndex = targetIndex;
        const itemsBeforeNewIndex = currentActiveItems
          .slice(0, targetIndex)
          .filter((item) => movingItemsSet.has(item.id)).length;
        adjustedIndex -= itemsBeforeNewIndex;

        const safeIndex = Math.min(
          Math.max(0, adjustedIndex),
          remainingItems.length,
        );
        const newItems = [
          ...remainingItems.slice(0, safeIndex),
          ...movingItems,
          ...remainingItems.slice(safeIndex),
        ];

        return {
          ...prev,
          [activeContainer]: newItems,
        };
      }

      return {
        ...prev,
        [activeContainer]: arrayMove(
          currentActiveItems,
          currentActiveIndex,
          targetIndex,
        ),
      };
    },
    [],
  );

  useEffect(() => {
    // Staff Level Optimization: Build a Map for O(1) lookup during drag
    const currentMap = latestStateRef.current;
    const newCache = new Map();
    for (const [trackId, cars] of Object.entries(currentMap)) {
      cars.forEach((car) => newCache.set(car.id, trackId));
    }
    containerCacheRef.current = newCache;

    findContainerRef.current = (id) => {
      if (String(id).startsWith("track-")) {
        return String(id).replace("track-", "");
      }
      return containerCacheRef.current.get(id) || null;
    };
  }, [processedCarsMap]);

  const handleDragStart = useCallback((event) => {
    originalStateRef.current = JSON.parse(
      JSON.stringify(latestStateRef.current),
    );
    isUpdatingRef.current = false;
    lastUpdateTimeRef.current = 0;
    isSavingRef.current = false;
  }, []);

  const handleDragOver = useCallback(
    (event) => {
      const now = Date.now();
      // PRINCIPAL ENGINEER RECOMMENDATION: Increased throttle for mobile performance
      // 16ms is roughly 60fps, which is too aggressive for many mobile devices during drag operations
      // with heavy state updates. 32ms (~30fps) is a sweet spot for "perceived smoothness" vs "cpu overhead".
      const throttleMs = window.innerWidth < 960 ? 48 : 24; // Principal Engineer recommendation: conservative throttle for complex grids
      if (now - lastUpdateTimeRef.current < throttleMs) {
        return;
      }
      lastUpdateTimeRef.current = now;

      // CRITICAL: Prevent concurrent updates
      if (isUpdatingRef.current) {
        return;
      }

      const { active, over } = event;
      const overId = over?.id;

      if (!overId || active.id === overId) return;

      const activeContainer = findContainerRef.current?.(active.id);
      const overContainer = findContainerRef.current?.(overId);

      if (!activeContainer || !overContainer) return;

      // Determine if we are moving a single item or a selection
      const currentSelectedIds = selectedShipmentIdsRef.current;
      const isMultiDrag =
        currentSelectedIds && currentSelectedIds.includes(active.id);
      const shipmentIdsToMove = isMultiDrag ? currentSelectedIds : [active.id];

      isUpdatingRef.current = true;

      setProcessedCarsMap((prev) => {
        const unlockUpdate = () => {
          setTimeout(() => {
            isUpdatingRef.current = false;
          }, 0);
        };

        const newState = getNextStateForMove({
          prev,
          active,
          over,
          overId,
          activeContainer,
          overContainer,
          shipmentIdsToMove,
          isMultiDrag,
        });

        latestStateRef.current = newState;
        unlockUpdate();
        return newState;
      });

      if (setDragOverInfo) setDragOverInfo(null);
    },
    [getNextStateForMove, setProcessedCarsMap, setDragOverInfo],
  );

  const handleDragEnd = useCallback(
    async (event) => {
      // CRITICAL: Prevent multiple simultaneous saves
      if (isSavingRef.current) {
        console.warn("Save already in progress, ignoring drag end");
        return;
      }

      const { active, over } = event;
      const overId = over?.id;

      // Use try-finally to ensure flags are always reset
      try {
        if (!over || !overId) {
          if (setLoading) setLoading(false);
          return;
        }

        const activeContainer = findContainerRef.current?.(active.id);
        const overContainer = findContainerRef.current?.(overId);
        if (!overContainer) {
          if (setLoading) setLoading(false);
          return;
        }

        if (activeContainer && overContainer) {
          const currentSelectedIds = selectedShipmentIdsRef.current;
          const isMultiDrag =
            currentSelectedIds && currentSelectedIds.includes(active.id);
          const shipmentIdsToMove = isMultiDrag
            ? currentSelectedIds
            : [active.id];

          setProcessedCarsMap((prev) => {
            const newState = getNextStateForMove({
              prev,
              active,
              over,
              overId,
              activeContainer,
              overContainer,
              shipmentIdsToMove,
              isMultiDrag,
            });
            latestStateRef.current = newState;
            return newState;
          });
        }

        const finalVisualState = latestStateRef.current;
        const targetList = finalVisualState[overContainer] || [];
        const finalIndex = targetList.findIndex((c) => c.id === active.id);

        if (finalIndex === -1) {
          setProcessedCarsMap(finalVisualState);
          if (setLoading) setLoading(false);
          return;
        }

        const originalContainer = findContainerRef.current?.(active.id);
        if (originalContainer === overContainer && originalStateRef.current) {
          const originalList = originalStateRef.current[overContainer] || [];
          const originalIndex = originalList.findIndex(
            (c) => c.id === active.id,
          );
          if (originalIndex === finalIndex) {
            if (setLoading) setLoading(false);
            return;
          }
        }

        // STAFF LEVEL OPTIMIZATION: Defer the heavy persistence logic
        // to the next tick. This allows dnd-kit to finish the drop animation
        // and React to clear the drag overlay BEFORE the CPU-intensive
        // backend sync begins.
        const activeId = active.id;
        const currentSelectedIds = selectedShipmentIdsRef.current;
        const hasSelection =
          currentSelectedIds && currentSelectedIds.includes(activeId);
        const shipmentIdsToMove =
          hasSelection && currentSelectedIds.length > 0
            ? currentSelectedIds
            : [activeId];

        const originalState = originalStateRef.current;
        originalStateRef.current = null;

        const schedulePostPaint = (cb) => {
          if (
            typeof window !== "undefined" &&
            typeof window.requestAnimationFrame === "function"
          ) {
            window.requestAnimationFrame(() => setTimeout(cb, 0));
            return;
          }
          setTimeout(cb, 0);
        };

        schedulePostPaint(async () => {
          try {
            const sourceTrackIds = (() => {
              if (!originalState || !shipmentIdsToMove?.length) return [];
              const shipmentIdSet = new Set(shipmentIdsToMove);
              return Object.keys(originalState).filter((trackId) => {
                const cars = originalState[trackId];
                return (
                  Array.isArray(cars) &&
                  cars.some((car) => shipmentIdSet.has(car.id))
                );
              });
            })();

            const syntheticEvent = {
              preventDefault: () => {},
              dataTransfer: {
                getData: () =>
                  JSON.stringify({ shipmentIds: shipmentIdsToMove }),
              },
            };

            isSavingRef.current = true;

            const affectedTrackIds = await handleTrackDropBase(
              syntheticEvent,
              overContainer,
              { index: finalIndex },
              originalState,
              finalVisualState,
            );

            if (typeof refreshTracks === "function") {
              const mergedTrackIds = new Set();
              if (affectedTrackIds && Array.isArray(affectedTrackIds)) {
                affectedTrackIds.forEach((id) => mergedTrackIds.add(id));
              }
              sourceTrackIds.forEach((id) => mergedTrackIds.add(id));
              if (overContainer) mergedTrackIds.add(overContainer);

              const validTrackIds = Array.from(mergedTrackIds).filter(
                (id) => id && id !== unspottedTrackId,
              );

              const emitRefresh = (ids) => {
                const payload = {
                  ids: ids || null,
                  ts: Date.now(),
                  nonce: `${Date.now()}`,
                };
                if (broadcastChannelRef.current)
                  broadcastChannelRef.current.postMessage(payload);
                try {
                  window.localStorage.setItem(
                    CROSS_TAB_REFRESH_KEY,
                    JSON.stringify(payload),
                  );
                } catch (e) {}
              };

              try {
                if (validTrackIds.length > 0) {
                  refreshTracks(validTrackIds);
                  emitRefresh(validTrackIds);
                } else {
                  refreshTracks();
                  emitRefresh(null);
                }
              } catch (err) {
                logError("handleDragEnd:refreshTracks", err, notify);
              }
            }

            if (typeof onSuccess === "function") {
              onSuccess();
            }
          } catch (error) {
            logError("handleDragEnd:saveError", error, notify);
            if (setLoading) setLoading(false);
          } finally {
            isSavingRef.current = false;
          }
        });
      } catch (error) {
        logError("handleDragEnd:mainError", error, notify);
      } finally {
        isUpdatingRef.current = false;
      }
    },
    [
      getNextStateForMove,
      handleTrackDropBase,
      setProcessedCarsMap,
      logError,
      notify,
      onSuccess,
      refreshTracks,
      unspottedTrackId,
      setLoading,
    ],
  );

  return { handleDragStart, handleDragEnd, handleDragOver };
};
